import React, { useState, useEffect, useCallback, useRef } from 'react';
import { BellIcon, EnvelopeIcon, TrashIcon, XMarkIcon } from '@heroicons/react/24/solid';
import { useDispatch, useSelector } from "react-redux";
import moment from "moment";
import { getNotifications, readNotification, deleteNotification, clearPendingDeletes, markForDelete } from "@/store/slices/notificationSlice";
import { getUserProfile } from "@/store/slices/userSlice";
import InfiniteScroll from 'react-infinite-scroll-component';
import { useNavigate } from "react-router-dom";
import Swal from "sweetalert2"
import { Toast } from 'flowbite-react';
import Toaster from '@/components/Toaster';
import { useTranslation } from 'react-i18next';


// Admin Side Notification


export function Notification({ onClose }) {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const [page, setPage] = useState(1);
    const { t } = useTranslation("notification");

    const { notifications, hasMore, pending_delete_ids, loading } = useSelector((state) => state.notification);

    useEffect(() => {
            setPage(1);
            setRemovedNotificationIds([]);
            dispatch(getNotifications({ page: page }));
    }, []);


    const handleKeyDown = useCallback((event) => {
        if (event.key === 'Escape') onClose();
    }, [ onClose]);

    useEffect(() => {
        document.addEventListener('keydown', handleKeyDown);
        return () => document.removeEventListener('keydown', handleKeyDown);
    }, [handleKeyDown]);


    const [removedNotificationIds, setRemovedNotificationIds] = useState([]);

    const removeNotification = (id) => {
        dispatch(markForDelete(id));
    };

    const deleteAllNotification = () => {
        const list = notifications?.notifications || [];

        if (list.length === 0) {
            Toaster.error(t("no_notifications_to_delete"));
            return;
        }

        Swal.fire({
            title: t("delete_all_confirmation_title"),
            text: t("delete_all_confirmation_text"),
            icon: "warning",
            showCancelButton: true,
            confirmButtonColor: "#3085d6",
            cancelButtonColor: "#d33",
            confirmButtonText: t("delete_all_confirmation_button")
        }).then((result) => {
            if (!result.isConfirmed) return;

            const ids = list.map(n => n.id);

            dispatch(deleteNotification(ids))
                .unwrap()
                .then((res) => {
                    if (res?.status) {
                        Toaster.success(t("delete_all_success"));
                        dispatch(getUserProfile());
                        setPage(1);
                        setRemovedNotificationIds([]);
                        dispatch(getNotifications({ page: page }));
                    }
                })
                .catch(() => {
                    Toaster.error(t("delete_all_fail"));
                });
        });
    };


    const handleMouseLeave = () => {
        if (removedNotificationIds?.length === 0) return;

        dispatch(deleteNotification({ ids: removedNotificationIds }));
    };

    const fetchMoreData = () => {
        const nextPage = page + 1;
        dispatch(getNotifications({ page: nextPage })).unwrap().then((res) => {
            setPage(nextPage);
        });
    };

    const close = () => {
        onClose();
        if (pending_delete_ids?.length > 0) {
            dispatch(deleteNotification(pending_delete_ids)).unwrap().then(() => {
                dispatch(clearPendingDeletes());
            });
        }
    }

    const goTopage = (data) => {
        let type = ['rfq', 'rfq_requested', 'discussion', 'quotation', 'chat','delivery_assignment'];
        if (type.includes(data?.type)) {
            if (data?.type === 'rfq' || data?.type === 'rfq_requested') {
                navigate('/dashboard/rfqs');
                close();
            } else if ((data?.type === 'discussion' || data?.type === 'quotation') && data?.reference_id) {
                navigate(`/dashboard/rfqs/${data?.reference_id}`, {
                    state: {
                        active_tab: data?.type === 'quotation' ? 'items' : 'chat',
                    },
                });
                close();
            } else if (data?.type === 'chat'){
                navigate(`/dashboard/chat`);
                close();
            }else if(data?.type === 'delivery_assignment'){
                navigate(`/dashboard/orders/${data?.notification_data?.order_id}`);
                close();
            }
        }
        if (data?.id) {
            dispatch(readNotification({ ids: [data?.id] })).unwrap().then((res) => {
                if (res?.status) {
                    dispatch(getUserProfile());
                }
            });
        }
    }


    return (
        <>

            {/* Right Drawer */}
            <div
                className={`fixed top-0 left-0 h-screen w-[420px] bg-white shadow-2xl z-50 transform transition-transform duration-500 ease-in-out
                    translate-x-0`}
                role="dialog"
                aria-modal="true"
            >
                {/* Header */}
                <header className="p-6 flex justify-between items-center border-b bg-gray-100">
                    <h2 className="text-xl font-bold text-gray-900">
                        {t("title", { total: notifications?.pagination?.total })}
                    </h2>
                    <button
                        onClick={() => deleteAllNotification()}
                        className="text-red-500 hover:text-gray-900 transition p-2 rounded-full hover:bg-gray-200"
                    >
                        {t("delete_all")}
                    </button>
                    <button
                        onClick={() => close()}
                        className="text-gray-500 hover:text-gray-900 transition p-2 rounded-full hover:bg-gray-200"
                    >
                        <XMarkIcon className="w-6 h-6" />
                    </button>
                  
                </header>

                {/* Notification List */}
                <main className="pt-4" onMouseLeave={handleMouseLeave}>
                    <InfiniteScroll
                        dataLength={notifications?.notifications?.length || 0}
                        next={fetchMoreData}
                        hasMore={hasMore}
                        height={window.innerHeight - 80}
                        loader={
                            <div role="status" className="text-center">
                                <svg aria-hidden="true" className="inline w-8 h-8 text-gray-200 animate-spin dark:text-gray-600 fill-green-500" viewBox="0 0 100 101" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z" fill="currentColor" />
                                    <path d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z" fill="currentFill" />
                                </svg>
                            </div>
                        }
                        endMessage={
                            <p className="text-center text-gray-400 py-4 text-sm mb-6">
                                {t("no_more_notifications")}
                            </p>
                        }
                    >
                        {(notifications?.notifications?.length !== 0) &&
                            <ul className="space-y-2">
                                {notifications?.notifications?.map((notification) => (
                                    <li key={notification.id} onClick={() => goTopage(notification)}>
                                        <NotificationItem
                                            notification={notification}
                                            onDelete={() => removeNotification(notification.id)}
                                        />
                                    </li>
                                ))}
                            </ul>
                        }
                    </InfiniteScroll>

                    {loading && <ul><li className="text-center"> <div role="status">
                        <svg aria-hidden="true" className="inline w-8 h-8 text-gray-200 animate-spin dark:text-gray-600 fill-green-500" viewBox="0 0 100 101" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z" fill="currentColor" />
                            <path d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z" fill="currentFill" />
                        </svg>
                    </div></li></ul>}

                    {(!loading && notifications?.notifications?.length === 0) &&
                        <div className="flex flex-col items-center justify-center h-full text-center py-12">
                            <BellIcon className="w-12 h-12 text-gray-300 mb-4" />
                            <p className="text-gray-500 text-lg">{t("all_caught_up")}</p>
                        </div>
                    }
                </main>
            </div>
        </>
    );
}
export default Notification;

const NotificationItem = ({ notification, onDelete }) => {

    const formattedDate = notification?.created_at 
        ? moment(notification.created_at).format("DD MMM YYYY, hh:mm A") 
        : "";

    return (
        <div className={`flex p-4 border-b border-gray-200 hover:bg-gray-100 transition duration-150 cursor-pointer rounded-lg mb-2 ${notification?.status === 'unread' ? "bg-green-50" : "bg-white"}`}>
            <div className="flex-shrink-0 mr-3 mt-1" onClick={() => onDelete(notification.id)}>
                <TrashIcon className="text-red-500 w-5 h-5" />
            </div>
            <div className="flex-grow">
                <div className="flex justify-between items-start">
                    <p className="text-gray-900 font-semibold text-base">{notification?.title}</p>
                    <span className="text-[11px] text-gray-500 font-medium whitespace-nowrap shrink-0">
                        {formattedDate}
                    </span>
                </div>
                <p className="text-gray-700 text-sm mt-1">{notification?.message}</p>
            </div>

        </div>
    );
};